import React from 'react';
import {buildSparkline, SPARK_BOX_DUAL} from './portalUsageState';

/**
 * La serie de 12 semanas en chico: la card del Panel y cada fila del ranking cross-cliente.
 *
 * Es el MISMO lenguaje visual que el gráfico grande del detalle, a propósito — la agencia tiene
 * que reconocer la forma de un cliente pasando del listado al detalle sin releer un eje.
 *
 * Serie entera en cero → un guion, no una línea plana. Una línea contra el piso se lee como un
 * dato, y "no usó nada" no es un valor bajo: es la ausencia del valor.
 *
 * Con `reportWeeks` dibuja las DOS superficies del portal apiladas —el chat arriba, los ingresos a
 * informes abajo—, cada una con su propio máximo (ver `SPARK_BOX_DUAL`). La prop es opcional a
 * propósito: sin ella el componente se comporta exactamente como antes, así que la card del Panel
 * no percibe el cambio.
 *
 * ⚠️ **Las dos series NO comparten criterio de zona horaria.** El chat se bucketea con el offset del
 * navegador (`weekly_counts_by_client(tzinfo)`); los ingresos a informes salen de `_date`, que ya es
 * la fecha de NEGOCIO y no se puede re-zonificar (`weekly_views_by_client`, sin tz). Para una agencia
 * fuera de `MONITORING_TIME_ZONE`, actividad del mismo instante puede caer en semanas distintas en
 * cada fila. No mueve ningún contador —sólo puede correr un punto—, pero un sparkline se lee por su
 * FORMA, así que conviene saberlo antes de sacar conclusiones de un desfase de una semana entre las
 * dos. Arreglarlo de verdad exige cambiar el grano diario de `PortalSurfaceActivity`.
 */
export function PortalSparkline({weeks, reportWeeks, emptyLabel = '—'}) {
    // `Array.isArray` y no `=== undefined`: `null` o cualquier otra cosa que no sea una serie tiene
    // que caer al modo simple, no dibujar media caja vacía.
    if (!Array.isArray(reportWeeks)) return renderSingle(buildSparkline(weeks), emptyLabel);

    const chat = buildSparkline(weeks, SPARK_BOX_DUAL);
    const reports = buildSparkline(reportWeeks, SPARK_BOX_DUAL);

    if (!chat && !reports) {
        return <span className="pu-spark__empty" aria-hidden="true">{emptyLabel}</span>;
    }

    // Las dos filas se renderizan siempre, aunque una esté vacía: sin el hueco reservado, la serie
    // que sí existe saltaría de posición según lo que haga la otra y la columna dejaría de
    // compararse entre filas.
    return (
        <span className="pu-spark-dual" aria-hidden="true">
            <span className="pu-spark-dual__row">{renderTrack(chat, 'chat')}</span>
            <span className="pu-spark-dual__row">{renderTrack(reports, 'rep')}</span>
        </span>
    );
}

function renderSingle(spark, emptyLabel) {
    if (!spark) return <span className="pu-spark__empty" aria-hidden="true">{emptyLabel}</span>;

    return (
        <svg
            className="pu-spark"
            viewBox={`0 0 ${spark.box.width} ${spark.box.height}`}
            aria-hidden="true"
            focusable="false"
        >
            <path className="pu-spark__area" d={spark.areaPath}/>
            <path className="pu-spark__line" d={spark.linePath}/>
            <circle className="pu-spark__last" cx={spark.last.x} cy={spark.last.y} r="2.3"/>
        </svg>
    );
}

function renderTrack(spark, tone) {
    if (!spark) return null;

    return (
        <svg
            className={`pu-spark -dual -${tone}`}
            viewBox={`0 0 ${spark.box.width} ${spark.box.height}`}
            focusable="false"
        >
            <path className="pu-spark__area" d={spark.areaPath}/>
            <path className="pu-spark__line" d={spark.linePath}/>
            <circle className="pu-spark__last" cx={spark.last.x} cy={spark.last.y} r="1.6"/>
        </svg>
    );
}

export default PortalSparkline;
